import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMigrationTimeout, useSigninFlow } from '.';
import {
  IApiCallStatus,
  icamService,
  IError,
  IICAMToken,
  logoutOkta,
  mapICAMTokenToUser,
  mapUserAuthenticated,
  useAlertContext,
  useFeature,
  useQuery,
  useUserContext,
} from '../common';
import { assocPath, path, always } from 'rambda';
import { compose } from 'lodash/fp';
import { left, right, fold, map as emap } from 'fp-ts/lib/Either';

export function useSignin() {
  let query = useQuery();
  let history = useNavigate();
  const signinFlow = useSigninFlow();
  const { setUser } = useUserContext();

  const [apiCallStatus, setApiCallStatus] = useState<IApiCallStatus>();
  const [message, setMessage] = useState<string>();

  const {
    setAlert,
    alert: { isOk },
  } = useAlertContext();

  const clearMigrationTimeout = useMigrationTimeout(apiCallStatus);

  useEffect(() => {
    const handleIcamError = compose(
      clearMigrationTimeout,
      () => setApiCallStatus(false),
      setMessage,
      path('message')
    );

    const handleIcamSuccess = fold<IError, IICAMToken, void>(
      handleIcamError,
      compose(
        clearMigrationTimeout,
        () => setApiCallStatus(true),
        setUser,
        mapICAMTokenToUser
      )
    );
    const run = (signinFlow: boolean) =>
      compose(
        emap(signin),
        (code: string | null) =>
          code !== null && signinFlow ? right(`${code}`) : left(''),
        query.get.bind(query)
      );

    const signin = compose(
      () => setApiCallStatus(null),
      (code: string) => {
        icamService(code).then(handleIcamSuccess);
      }
    );

    run(signinFlow)('code');
  }, [query, signinFlow, setUser, clearMigrationTimeout]);

  useEffect(() => {
    switch (apiCallStatus) {
      case null:
        setAlert(a => ({
          ...a,
          title: 'CSOS Sign In',
          message: () => (
            <>
              <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />
            </>
          ),
          okAction: false,
          cancelAction: false,
          isOpen: true,
        }));
        break;

      case true:
        setAlert(assocPath('isOpen', false));
        break;

      case void 0:
        break;

      case false:
        message === 'The authorization code is invalid or has expired.'
          ? // TODO revert once we understand and fix this okta error
            // ? (window.location.href = '/')
            setAlert(a => ({
              ...a,
              title: 'CSOS Sign In',
              message: () => (
                <>
                  <FontAwesomeIcon icon={faTimes} size="1x" fixedWidth />{' '}
                  {message}
                </>
              ),
              okAction: true,
              cancelAction: false,
              isOpen: true,
            }))
          : setAlert(a => ({
              ...a,
              title: 'CSOS Sign In',
              message: () => (
                <>
                  <FontAwesomeIcon icon={faTimes} size="1x" fixedWidth />{' '}
                  {message}
                </>
              ),
              okAction: true,
              cancelAction: false,
              isOpen: true,
            }));
        break;

      default:
        setAlert(a => ({
          ...a,
          title: 'CSOS Sign In',
          message: () => <>unknown</>,
          okAction: true,
          cancelAction: false,
          isOpen: true,
        }));
        break;
    }
  }, [apiCallStatus, message, setAlert]);

  useEffect(() => {
    compose(
      emap(
        compose(
          setUser,
          always(mapUserAuthenticated(true)), // moving this to mapICAMTokenToUser unmounts Landing; CSOS Sign In alert spins for ever

          () => history('/my-information')
        )
      ),
      apiCallStatus =>
        apiCallStatus ? right(apiCallStatus) : left(apiCallStatus)
    )(apiCallStatus);
  }, [apiCallStatus, history, setUser]);

  useFeature({ apiCallStatus });

  useEffect(() => {
    compose(
      emap(compose(logoutOkta, () => setMessage(void 0))),
      (message: string | undefined, isOk: boolean) =>
        message !== void 0 && isOk ? right(void 0) : left(void 0)
    )(message, isOk);
  }, [message, isOk, setMessage]);
}
